import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Seo from '../components/Seo';
import {
  Search, MessageCircle, Phone, Mail, ChevronDown,
  Users, Car, CreditCard, MapPin, HelpCircle, ArrowRight,
  Shield, FileText, Clock, ThumbsUp, CheckCircle2,
} from 'lucide-react';
import HeaderSection from './HeaderSection';
import Footer from './Footer';
import ScrollToTop from './ScrollToTop';

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-60px' },
  transition: { duration: 0.45, delay },
});

const COLOR_CLASSES = {
  primary: { text: 'text-primary', bg: 'bg-primary-tint dark:bg-primary/10', border: 'border-primary/25', solid: 'bg-primary' },
  teal: { text: 'text-teal-dark dark:text-teal', bg: 'bg-teal-tint dark:bg-teal/10', border: 'border-teal/25', solid: 'bg-teal-dark' },
  accent: { text: 'text-accent-dark dark:text-accent', bg: 'bg-accent-tint dark:bg-accent/10', border: 'border-accent/25', solid: 'bg-accent-dark' },
  ink: { text: 'text-ink dark:text-dark-ink', bg: 'bg-surface-alt dark:bg-white/10', border: 'border-line dark:border-dark-line', solid: 'bg-ink' },
};

const CATEGORIES = [
  { id: 'Getting Started', icon: Users, label: 'Getting Started', desc: 'New to TotoWala? Learn the basics', color: 'primary' },
  { id: 'Booking Rides', icon: Car, label: 'Booking Rides', desc: 'How to book and manage your rides', color: 'teal' },
  { id: 'Payments', icon: CreditCard, label: 'Payments', desc: 'Payment methods and billing questions', color: 'accent' },
  { id: 'Driver Support', icon: MapPin, label: 'Driver Support', desc: 'Information for TotoWala drivers', color: 'ink' },
];

const FAQS = [
  { category: 'Getting Started', question: 'How do I create a TotoWala account?', answer: 'Download the TotoWala app from Google Play Store, tap "Sign Up", enter your mobile number, verify with OTP, and complete your profile with basic information.' },
  { category: 'Getting Started', question: "How does TotoWala's commission work?", answer: 'TotoWala currently offers zero commission as a launch offer, drivers keep the maximum possible share of their earnings. We are committed to always maintaining the lowest commission in the market, with no platform fees or hidden charges.' },
  { category: 'Booking Rides', question: 'How do I book a ride?', answer: 'Open the app, enter your pickup and destination locations, choose your ride type, confirm the booking, and wait for a driver to accept your request.' },
  { category: 'Booking Rides', question: 'Can I cancel a ride?', answer: 'Yes, you can cancel rides before the driver arrives without penalty. Cancellations after driver arrival may incur a small cancellation fee.' },
  { category: 'Payments', question: 'What payment methods are accepted?', answer: 'We accept cash, UPI, debit/credit cards, and digital wallets. All payments are processed securely through our app.' },
  { category: 'Payments', question: 'How is the fare calculated?', answer: 'Fares are calculated based on distance, time, and current market rates. Our transparent pricing means no hidden fees and near-zero surge pricing.' },
  { category: 'Driver Support', question: 'How do I become a TotoWala driver?', answer: 'Download the TotoWala Driver app, complete registration with required documents (licence, RC, insurance), undergo verification, and start earning with the lowest commission in Bhagalpur.' },
  { category: 'Driver Support', question: 'What documents do I need as a driver?', answer: 'You need a valid driving licence, vehicle registration certificate (RC), insurance documents, and Aadhaar card for identity verification.' },
];

const POPULAR_SEARCHES = ['Driver earnings', 'Cancel a ride', 'Become a driver', 'Payment methods', 'Track my ride'];

const CONTACT_METHODS = [
  { icon: Mail, label: 'Email Support', desc: 'Detailed help for complex issues', cta: 'Send Email', href: 'mailto:contact@totowala.in', responseTime: '< 24 hrs', color: 'primary' },
  { icon: MessageCircle, label: 'Live Chat', desc: 'Real-time help through the app', cta: 'Start Chat', href: null, responseTime: '< 5 min', color: 'teal' },
  { icon: Phone, label: 'Phone Support', desc: 'Speak directly with our team', cta: 'Call Now', href: 'tel:+918088257606', responseTime: 'Instant', color: 'ink' },
];

const QUICK_LINKS = [
  { href: '/safety', icon: Shield, label: 'Safety Guidelines', desc: 'Safety measures and tips for secure rides.', color: 'primary' },
  { href: '/terms-conditions', icon: FileText, label: 'Terms & Conditions', desc: 'Terms of service and user agreements.', color: 'teal' },
];

const HelpCenter = () => {
  const [openFaq, setOpenFaq] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');

  const toggleFaq = (index) => setOpenFaq(openFaq === index ? null : index);

  const filteredFaqs = FAQS.filter((faq) => {
    const matchSearch = !searchTerm || faq.question.toLowerCase().includes(searchTerm.toLowerCase()) || faq.answer.toLowerCase().includes(searchTerm.toLowerCase());
    const matchCategory = activeCategory === 'All' || faq.category === activeCategory;
    return matchSearch && matchCategory;
  });

  const catColor = (id) => CATEGORIES.find((c) => c.id === id)?.color || 'primary';
  const catCount = (id) => FAQS.filter((f) => f.category === id).length;
  const hasFilters = searchTerm || activeCategory !== 'All';

  return (
    <>
      <Seo
        title="Help Center - TotoWala | FAQs & Support for Indian Rides"
        description="Find answers to frequently asked questions about TotoWala ride booking. Get help with payments, bookings, driver support, and more across India."
        canonical="/help"
        noIndex={false}
        schemaMarkup={{
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: FAQS.map((faq) => ({
            '@type': 'Question',
            name: faq.question,
            acceptedAnswer: { '@type': 'Answer', text: faq.answer },
          })),
        }}
      />
      <div className="min-h-screen bg-white dark:bg-dark-bg">
        <HeaderSection />

        {/* ── Hero ── */}
        <section className="bg-surface-alt dark:bg-dark-surface-alt py-12 lg:py-14 px-4">
          <div className="max-w-[760px] mx-auto text-center">
            <motion.h1
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }}
              className="font-display text-3xl sm:text-4xl font-semibold leading-tight text-ink dark:text-dark-ink mb-4"
            >
              Help Center
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.06 }}
              className="text-base text-ink-soft dark:text-dark-ink-soft leading-relaxed max-w-lg mx-auto mb-7"
            >
              Find answers instantly. Browse our knowledge base, search FAQs, or connect with our support team directly.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.1 }}
              className="relative max-w-md mx-auto mb-4"
            >
              <Search size={17} className="absolute left-4 top-1/2 -translate-y-1/2 text-ink-muted pointer-events-none" />
              <input
                type="text"
                placeholder="Search for help..."
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setOpenFaq(null); }}
                className="w-full pl-11 pr-4 py-3.5 rounded-control bg-white dark:bg-dark-surface border border-line dark:border-dark-line text-sm text-ink dark:text-dark-ink placeholder:text-ink-muted outline-none focus:border-primary transition-colors"
              />
              {searchTerm && (
                <button onClick={() => setSearchTerm('')} className="absolute right-4 top-1/2 -translate-y-1/2 text-xs font-semibold text-ink-muted">
                  Clear
                </button>
              )}
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.4, delay: 0.15 }}
              className="flex flex-wrap items-center justify-center gap-2"
            >
              <span className="text-xs text-ink-muted">Popular:</span>
              {POPULAR_SEARCHES.map((term) => (
                <button
                  key={term}
                  onClick={() => { setSearchTerm(term); setActiveCategory('All'); }}
                  className="px-3 py-1.5 rounded-full text-xs font-medium bg-white dark:bg-dark-surface border border-line dark:border-dark-line text-ink-soft dark:text-dark-ink-soft hover:border-primary hover:text-primary transition-colors"
                >
                  {term}
                </button>
              ))}
            </motion.div>
          </div>
        </section>

        {/* ── Categories ── */}
        <section className="bg-white dark:bg-dark-bg py-12 lg:py-14 px-4">
          <div className="max-w-[1200px] mx-auto">
            <h2 className="font-display text-2xl sm:text-3xl font-semibold text-center text-ink dark:text-dark-ink mb-2">
              Browse by Category
            </h2>
            <p className="text-base text-ink-soft dark:text-dark-ink-soft text-center mb-8">Select a topic to filter FAQs instantly.</p>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {CATEGORIES.map(({ id, icon: Icon, label, desc, color }, i) => {
                const isActive = activeCategory === id;
                const c = COLOR_CLASSES[color];
                return (
                  <motion.button
                    key={id} {...fadeUp(i * 0.05)}
                    onClick={() => setActiveCategory(isActive ? 'All' : id)}
                    className={`text-left p-5 rounded-card border transition-colors ${isActive ? `${c.bg} ${c.border}` : 'bg-white dark:bg-dark-surface border-line dark:border-dark-line'}`}
                  >
                    <div className="flex justify-end mb-3">
                      <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${isActive ? `${c.text} ${c.bg}` : 'text-ink-muted bg-surface-alt dark:bg-white/5'}`}>
                        {catCount(id)} FAQ{catCount(id) !== 1 ? 's' : ''}
                      </span>
                    </div>
                    <div className={`flex items-center justify-center w-11 h-11 rounded-control mb-4 ${isActive ? c.solid : c.bg} ${isActive ? '' : `border ${c.border}`}`}>
                      <Icon size={20} className={isActive ? 'text-white' : c.text} />
                    </div>
                    <h3 className="text-sm font-bold text-ink dark:text-dark-ink mb-1">{label}</h3>
                    <p className="text-xs text-ink-muted leading-relaxed">{desc}</p>
                    {isActive && (
                      <div className={`mt-3 inline-flex items-center gap-1.5 text-xs font-semibold ${c.text}`}>
                        <CheckCircle2 size={12} /> Active filter
                      </div>
                    )}
                  </motion.button>
                );
              })}
            </div>
          </div>
        </section>

        {/* ── FAQs ── */}
        <section className="bg-surface-alt dark:bg-dark-surface-alt py-12 lg:py-14 px-4">
          <div className="max-w-[760px] mx-auto">
            <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
              <div>
                <h2 className="font-display text-2xl sm:text-3xl font-semibold text-ink dark:text-dark-ink mb-1.5">
                  Frequently Asked Questions
                </h2>
                <p className="text-sm text-ink-muted">
                  {hasFilters ? `${filteredFaqs.length} result${filteredFaqs.length !== 1 ? 's' : ''} found` : `${FAQS.length} questions across ${CATEGORIES.length} categories`}
                </p>
              </div>
              {hasFilters && (
                <button
                  onClick={() => { setSearchTerm(''); setActiveCategory('All'); setOpenFaq(null); }}
                  className="px-3.5 py-1.5 rounded-full text-xs font-semibold border border-line dark:border-dark-line text-ink-soft dark:text-dark-ink-soft hover:border-primary hover:text-primary transition-colors"
                >
                  Reset filters
                </button>
              )}
            </div>

            <div className="flex flex-col gap-2.5">
              <AnimatePresence mode="popLayout">
                {filteredFaqs.map((faq, index) => {
                  const isOpen = openFaq === index;
                  const color = catColor(faq.category);
                  const c = COLOR_CLASSES[color];
                  const cat = CATEGORIES.find((cc) => cc.id === faq.category);
                  const CatIcon = cat?.icon;
                  return (
                    <motion.div
                      key={`${faq.category}-${faq.question}`}
                      layout
                      initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, scale: 0.97 }}
                      transition={{ duration: 0.25 }}
                      className={`rounded-card bg-white dark:bg-dark-surface border overflow-hidden transition-colors ${isOpen ? `${c.border}` : 'border-line dark:border-dark-line'}`}
                    >
                      <button onClick={() => toggleFaq(index)} className="w-full flex items-center gap-3.5 p-4 text-left">
                        <div className={`w-9 h-9 rounded-control flex items-center justify-center shrink-0 ${isOpen ? c.solid : c.bg}`}>
                          {CatIcon && <CatIcon size={16} className={isOpen ? 'text-white' : c.text} />}
                        </div>
                        <h3 className="flex-1 text-sm font-semibold text-ink dark:text-dark-ink leading-snug">{faq.question}</h3>
                        <motion.div animate={{ rotate: isOpen ? 180 : 0 }} transition={{ duration: 0.2 }} className="shrink-0">
                          <ChevronDown size={16} className={isOpen ? c.text : 'text-ink-muted'} />
                        </motion.div>
                      </button>

                      <AnimatePresence>
                        {isOpen && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.25 }} className="overflow-hidden"
                          >
                            <div className={`mx-4 mb-4 rounded-control px-4 py-3.5 ${c.bg}`}>
                              <p className="text-sm text-ink-soft dark:text-dark-ink-soft leading-relaxed">{faq.answer}</p>
                            </div>
                            <div className="flex items-center gap-2.5 px-4 pb-4">
                              <span className="text-xs text-ink-muted">Was this helpful?</span>
                              <button className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border border-line dark:border-dark-line text-ink-muted hover:border-teal hover:text-teal-dark dark:hover:text-teal transition-colors">
                                <ThumbsUp size={11} /> Yes
                              </button>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </div>

            {filteredFaqs.length === 0 && (
              <div className="text-center py-12 px-6 rounded-card bg-white dark:bg-dark-surface border border-line dark:border-dark-line">
                <div className="w-12 h-12 rounded-full bg-surface-alt dark:bg-white/5 flex items-center justify-center mx-auto mb-4">
                  <Search size={20} className="text-ink-muted" />
                </div>
                <h3 className="text-base font-bold text-ink dark:text-dark-ink mb-2">No results found</h3>
                <p className="text-sm text-ink-muted mb-5">Try different keywords or reset your filters.</p>
                <button
                  onClick={() => { setSearchTerm(''); setActiveCategory('All'); }}
                  className="px-5 py-2.5 rounded-full bg-primary text-white text-sm font-semibold hover:bg-primary-dark transition-colors"
                >
                  Reset filters
                </button>
              </div>
            )}
          </div>
        </section>

        {/* ── Still need help ── */}
        <section className="bg-white dark:bg-dark-bg py-12 lg:py-14 px-4">
          <div className="max-w-[760px] mx-auto">
            <h2 className="font-display text-2xl sm:text-3xl font-semibold text-center text-ink dark:text-dark-ink mb-2">
              Still Need Help?
            </h2>
            <p className="text-base text-ink-soft dark:text-dark-ink-soft text-center mb-8">Choose the fastest way to reach our support team.</p>

            <div className="flex flex-col gap-3">
              {CONTACT_METHODS.map(({ icon: Icon, label, desc, cta, href, responseTime, color }, i) => {
                const c = COLOR_CLASSES[color];
                const content = (
                  <div className="flex items-center gap-4 p-5 flex-wrap">
                    <div className={`w-12 h-12 rounded-control flex items-center justify-center shrink-0 ${c.solid}`}>
                      <Icon size={20} className="text-white" />
                    </div>
                    <div className="flex-1 min-w-[160px]">
                      <div className="flex items-center gap-2.5 flex-wrap mb-0.5">
                        <h3 className="text-sm font-bold text-ink dark:text-dark-ink">{label}</h3>
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-teal-tint dark:bg-teal/10 text-teal-dark dark:text-teal">
                          <Clock size={10} /> {responseTime}
                        </span>
                      </div>
                      <p className="text-xs text-ink-muted">{desc}</p>
                    </div>
                    <span className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold text-white shrink-0 ${c.solid}`}>
                      {cta} <ArrowRight size={13} />
                    </span>
                  </div>
                );
                return (
                  <motion.div key={label} {...fadeUp(i * 0.06)} className="rounded-card bg-surface-alt dark:bg-dark-surface border border-line dark:border-dark-line overflow-hidden">
                    {href ? <a href={href}>{content}</a> : <button className="w-full text-left">{content}</button>}
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* ── Quick links ── */}
        <section className="bg-surface-alt dark:bg-dark-surface-alt py-12 lg:py-14 px-4">
          <div className="max-w-[760px] mx-auto">
            <h2 className="font-display text-2xl sm:text-3xl font-semibold text-center text-ink dark:text-dark-ink mb-8">
              Quick Links
            </h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {QUICK_LINKS.map(({ href, icon: Icon, label, desc, color }, i) => {
                const c = COLOR_CLASSES[color];
                return (
                  <motion.a key={href} href={href} {...fadeUp(i * 0.06)} className="flex items-center gap-4 p-5 rounded-card bg-white dark:bg-dark-surface border border-line dark:border-dark-line">
                    <div className={`w-12 h-12 rounded-control flex items-center justify-center shrink-0 ${c.bg} border ${c.border}`}>
                      <Icon size={20} className={c.text} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-bold text-ink dark:text-dark-ink mb-0.5">{label}</h3>
                      <p className="text-xs text-ink-muted leading-relaxed">{desc}</p>
                    </div>
                    <ArrowRight size={16} className="text-ink-muted shrink-0" />
                  </motion.a>
                );
              })}
            </div>
          </div>
        </section>

        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
};

export default HelpCenter;
